import React, { useState, useEffect } from 'react';
import { X, GitCompare, ArrowLeftRight, ChevronLeft, ChevronRight, Maximize2, ZoomIn, Download } from 'lucide-react';
import CustomLoader from '../../../shared/components/ui/CustomLoader';
import { comparePptVersions } from '../utils/pptVersionService';
import { extractApiError } from '../utils/apiErrorUtils';
import './PptVersionModals.css';

// Placeholder Slide Image
const SlidePreview = ({ versionMeta, slide }) => {
    if (!slide) {
        return (
            <div className="cvp-slide-placeholder empty" style={{ background: '#F1F5F9', border: '1px dashed #CBD5E1', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: '300px', borderRadius: '8px' }}>
                <p style={{ color: '#64748B' }}>Slide not present in this version</p>
            </div>
        );
    }

    // Fallback if preview_url is not available yet
    if (!slide.preview_url) {
        return (
            <div className="cvp-slide-placeholder" style={{ position: 'relative', background: '#FFFFFF', border: '1px solid #E2E8F0', padding: '2rem', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: '300px', borderRadius: '8px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
                <div style={{ position: 'absolute', top: 0, left: 0, width: '4px', height: '100%', background: '#FF5526' }} />
                <h3 style={{ fontSize: '1.25rem', color: '#1E293B', marginBottom: '0.5rem', textAlign: 'center' }}>{slide.title || 'Untitled Slide'}</h3>
                <p style={{ color: '#64748B', fontSize: '0.85rem' }}>{versionMeta ? `Version ${versionMeta.version_number}` : ''}</p>
                <p style={{ color: '#94A3B8', fontSize: '0.75rem', marginTop: '1rem' }}>(Real slide images will be available when backend rendering is connected)</p>
            </div>
        );
    }

    return (
        <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <img src={slide.preview_url} alt={slide.title} style={{ maxWidth: '100%', height: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', borderRadius: '8px' }} />
        </div>
    );
};

const CompareVersionsModal = ({ rfpId, versions, defaultVersion, currentVersionNumber, onClose }) => {
    const latestVersion = versions.find(v => v.version_number === currentVersionNumber) || versions[0];
    const [versionA, setVersionA] = useState(latestVersion?.version_number ?? '');
    const [versionB, setVersionB] = useState(defaultVersion?.version_number ?? '');
    const [comparing, setComparing] = useState(false);

    // Normalized arrays of slides for comparison
    const [slidesA, setSlidesA] = useState([]);
    const [slidesB, setSlidesB] = useState([]);
    const [comparisonList, setComparisonList] = useState([]); // Array of { slideA, slideB, status, diff }

    const [error, setError] = useState('');
    const [activeFilter, setActiveFilter] = useState('all'); // all, different, added, removed

    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);

    // Flatten sections into a 1D array of slides
    const flattenSlides = (sections) => {
        if (!sections) return [];
        let flattened = [];
        sections.forEach(section => {
            if (section.slides && Array.isArray(section.slides)) {
                section.slides.forEach(slide => {
                    flattened.push({
                        ...slide,
                        section_id: section.id,
                        section_name: section.title
                    });
                });
            }
        });
        return flattened;
    };

    // Auto compare when versions change
    useEffect(() => {
        const handleCompare = async () => {
            if (!versionA || !versionB || Number(versionA) === Number(versionB)) {
                setComparisonList([]);
                return;
            }
            setComparing(true);
            setError('');
            try {
                const result = await comparePptVersions(rfpId, versionA, versionB);

                // Extract slides
                const aSlides = flattenSlides(result?.version_a?.sections || result?.v1_data?.sections || []);
                const bSlides = flattenSlides(result?.version_b?.sections || result?.v2_data?.sections || []);

                setSlidesA(aSlides);
                setSlidesB(bSlides);

                // Compare Logic (Basic structural comparison by slide_id)
                let compList = [];
                const bMap = new Map(bSlides.map(s => [s.slide_id, s]));

                // Find all in A (Current) and see if they exist in B
                aSlides.forEach(sA => {
                    const sB = bMap.get(sA.slide_id);
                    let status = 'added'; // In current, but not in old
                    if (sB) {
                        // Very basic check, stringify content
                        const cA = JSON.stringify(sA.content || {});
                        const cB = JSON.stringify(sB.content || {});
                        status = cA === cB ? 'no_change' : 'updated';
                        bMap.delete(sA.slide_id); // Mark as processed
                    }
                    compList.push({ slideA: sA, slideB: sB, status });
                });

                // Find remaining in B (Old) that are not in A
                bMap.forEach(sB => {
                    compList.push({ slideA: null, slideB: sB, status: 'removed' });
                });

                setComparisonList(compList);
                setCurrentSlideIndex(0);

            } catch (err) {
                console.error('Compare error:', err);
                setError(extractApiError(err, 'Comparison failed or not available.'));
            } finally {
                setComparing(false);
            }
        };

        if (versionA && versionB) {
            handleCompare();
        }
    }, [versionA, versionB, rfpId]);

    const getVersionMeta = (num) => versions.find(v => v.version_number === Number(num));
    const metaA = getVersionMeta(versionA);
    const metaB = getVersionMeta(versionB);

    // Apply Filters
    const filteredComparisonList = comparisonList.filter(item => {
        if (activeFilter === 'all') return true;
        if (activeFilter === 'different') return item.status === 'updated';
        if (activeFilter === 'added') return item.status === 'added';
        if (activeFilter === 'removed') return item.status === 'removed';
        return true;
    });

    const currentComp = filteredComparisonList[currentSlideIndex];
    const totalSlides = filteredComparisonList.length;

    const handlePrevSlide = () => {
        if (currentSlideIndex > 0) setCurrentSlideIndex(currentSlideIndex - 1);
    };

    const handleNextSlide = () => {
        if (currentSlideIndex < totalSlides - 1) setCurrentSlideIndex(currentSlideIndex + 1);
    };

    const handleVersionChange = (side, val) => {
        if (!val) return;
        const numVal = Number(val);
        if (side === 'A') setVersionA(numVal);
        if (side === 'B') setVersionB(numVal);
    };

    return (
        <div className="pvm-overlay" onClick={onClose} style={{ backdropFilter: 'blur(5px)' }}>
            <div className="pvm-modal pvm-modal-fullscreen" onClick={(e) => e.stopPropagation()}>

                {/* --- Header --- */}
                <div className="pvm-header-row">
                    <div className="pvm-header-left">
                        <div className="pvm-header-icon" style={{ background: '#FFF1EC', padding: '6px', borderRadius: '8px' }}>
                            <GitCompare size={22} color="#FF5526" />
                        </div>
                        <div>
                            <h3 style={{ margin: '0 0 4px', fontSize: '1.1rem', color: '#0F172A' }}>Compare Versions</h3>
                            <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748B' }}>Select two versions to compare</p>
                        </div>
                    </div>
                    <div className="pvm-header-right" style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>

                        <button onClick={onClose} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: '4px', display: 'flex' }}>
                            <X size={20} color="#64748B" />
                        </button>
                    </div>
                </div>

                {error && <div style={{ padding: '12px 24px', background: '#FEF2F2', color: '#EF4444', borderBottom: '1px solid #FEE2E2', fontSize: '0.85rem' }}>{error}</div>}

                {/* --- Version Selectors --- */}
                <div className="pvm-selectors-row" style={{ padding: '24px 24px 16px', display: 'flex', gap: '32px', borderBottom: '1px solid #E2E8F0' }}>
                    <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.85rem', fontWeight: 600, color: '#1E293B' }}>Current Version</label>
                        <select
                            value={versionA}
                            onChange={(e) => handleVersionChange('A', e.target.value)}
                            style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #CBD5E1', background: '#F8F9FA', fontSize: '0.85rem', outline: 'none' }}
                        >
                            <option value="">Select version</option>
                            {versions.map((v) => (
                                <option key={`a-${v.version_number}`} value={v.version_number}>
                                    Version {v.version_number} {v.version_number === currentVersionNumber ? '(Current)' : ''}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div style={{ paddingTop: '32px' }}>
                        <ArrowLeftRight size={20} color="#94A3B8" />
                    </div>

                    <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.85rem', fontWeight: 600, color: '#1E293B' }}>Compare With</label>
                        <select
                            value={versionB}
                            onChange={(e) => handleVersionChange('B', e.target.value)}
                            style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #CBD5E1', background: '#F8F9FA', fontSize: '0.85rem', outline: 'none' }}
                        >
                            <option value="">Select version</option>
                            {versions.map((v) => (
                                <option key={`b-${v.version_number}`} value={v.version_number}>
                                    Version {v.version_number} {v.version_number === currentVersionNumber ? '(Current)' : ''}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* --- Body: Slide Comparison --- */}
                <div className="pvm-body-fullscreen" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#F8F9FA' }}>
                    {comparing ? (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <CustomLoader size={30} color="#FF5526" />
                        </div>
                    ) : totalSlides > 0 ? (
                        <div className="pvm-split-view" style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                            {/* Left Pane (Version A) */}
                            <div className="pvm-pane" style={{ flex: 1, display: 'flex', flexDirection: 'column', borderRight: '1px solid #E2E8F0', background: '#FFFFFF', overflow: 'hidden' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #E2E8F0' }}>
                                    <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1E293B' }}>
                                        Version {versionA} {versionA === currentVersionNumber ? '(Current)' : ''}
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.8rem', color: '#64748B' }}>
                                            <span style={{ background: '#F1F5F9', padding: '4px 8px', borderRadius: '4px', color: '#1E293B', fontWeight: 500, border: '1px solid #E2E8F0' }}>{currentSlideIndex + 1}</span> / {totalSlides}
                                            <button onClick={handlePrevSlide} disabled={currentSlideIndex === 0} style={{ border: 'none', background: 'transparent', cursor: currentSlideIndex === 0 ? 'not-allowed' : 'pointer', opacity: currentSlideIndex === 0 ? 0.5 : 1, padding: '2px', display: 'flex' }}><ChevronLeft size={16} /></button>
                                            <button onClick={handleNextSlide} disabled={currentSlideIndex === totalSlides - 1} style={{ border: 'none', background: 'transparent', cursor: currentSlideIndex === totalSlides - 1 ? 'not-allowed' : 'pointer', opacity: currentSlideIndex === totalSlides - 1 ? 0.5 : 1, padding: '2px', display: 'flex' }}><ChevronRight size={16} /></button>
                                        </div>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                                    {/* Thumbnails */}
                                    <div style={{ width: '110px', background: '#FFFFFF', borderRight: '1px solid #E2E8F0', padding: '12px', display: 'flex', flexDirection: 'column', gap: '12px', overflowY: 'auto' }}>
                                        {filteredComparisonList.map((comp, idx) => (
                                            <div
                                                key={idx}
                                                onClick={() => setCurrentSlideIndex(idx)}
                                                style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: '4px' }}
                                            >
                                                <div style={{ fontSize: '0.7rem', color: '#64748B', fontWeight: 600 }}>{idx + 1}</div>
                                                <div style={{
                                                    aspectRatio: '16/9', background: '#F8F9FA', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', color: '#94A3B8', overflow: 'hidden',
                                                    border: idx === currentSlideIndex ? '2px solid #FF5526' : '1px solid #E2E8F0'
                                                }}>
                                                    {comp.slideA ? (
                                                        comp.slideA.preview_url ? <img src={comp.slideA.preview_url} alt="Thumb" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : 'Slide ' + (idx + 1)
                                                    ) : (
                                                        <span style={{ color: '#EF4444', fontSize: '10px' }}>Removed</span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    {/* Main Viewer - Scrollable */}
                                    <div style={{ flex: 1, padding: '24px', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', background: '#F8F9FA', overflowY: 'auto' }}>
                                        <SlidePreview versionMeta={metaA} slide={currentComp?.slideA} />
                                    </div>
                                </div>
                            </div>

                            {/* Right Pane (Version B) */}
                            <div className="pvm-pane" style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#FFFFFF', overflow: 'hidden' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #E2E8F0' }}>
                                    <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1E293B' }}>
                                        Version {versionB}
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.8rem', color: '#64748B' }}>
                                            <span style={{ background: '#F1F5F9', padding: '4px 8px', borderRadius: '4px', color: '#1E293B', fontWeight: 500, border: '1px solid #E2E8F0' }}>{currentSlideIndex + 1}</span> / {totalSlides}
                                            <button onClick={handlePrevSlide} disabled={currentSlideIndex === 0} style={{ border: 'none', background: 'transparent', cursor: currentSlideIndex === 0 ? 'not-allowed' : 'pointer', opacity: currentSlideIndex === 0 ? 0.5 : 1, padding: '2px', display: 'flex' }}><ChevronLeft size={16} /></button>
                                            <button onClick={handleNextSlide} disabled={currentSlideIndex === totalSlides - 1} style={{ border: 'none', background: 'transparent', cursor: currentSlideIndex === totalSlides - 1 ? 'not-allowed' : 'pointer', opacity: currentSlideIndex === totalSlides - 1 ? 0.5 : 1, padding: '2px', display: 'flex' }}><ChevronRight size={16} /></button>
                                        </div>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                                    {/* Thumbnails */}
                                    <div style={{ width: '110px', background: '#FFFFFF', borderRight: '1px solid #E2E8F0', padding: '12px', display: 'flex', flexDirection: 'column', gap: '12px', overflowY: 'auto' }}>
                                        {filteredComparisonList.map((comp, idx) => (
                                            <div
                                                key={idx}
                                                onClick={() => setCurrentSlideIndex(idx)}
                                                style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: '4px' }}
                                            >
                                                <div style={{ fontSize: '0.7rem', color: '#64748B', fontWeight: 600 }}>{idx + 1}</div>
                                                <div style={{
                                                    aspectRatio: '16/9', background: '#F8F9FA', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', color: '#94A3B8', overflow: 'hidden',
                                                    border: idx === currentSlideIndex ? '2px solid #FF5526' : '1px solid #E2E8F0'
                                                }}>
                                                    {comp.slideB ? (
                                                        comp.slideB.preview_url ? <img src={comp.slideB.preview_url} alt="Thumb" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : 'Slide ' + (idx + 1)
                                                    ) : (
                                                        <span style={{ color: '#10B981', fontSize: '10px' }}>Added</span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    {/* Main Viewer - Scrollable */}
                                    <div style={{ flex: 1, padding: '24px', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', background: '#F8F9FA', overflowY: 'auto' }}>
                                        <SlidePreview versionMeta={metaB} slide={currentComp?.slideB} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748B' }}>
                            No slides to display for this filter.
                        </div>
                    )}
                </div>

                {/* --- Footer --- */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', padding: '16px 24px', borderTop: '1px solid #E2E8F0', background: '#FFFFFF' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <span style={{ fontSize: '0.8rem', color: '#64748B', fontWeight: 600 }}>Filters</span>
                        <div style={{ display: 'flex', background: '#F1F5F9', borderRadius: '8px', padding: '4px' }}>
                            <button
                                onClick={() => { setActiveFilter('all'); setCurrentSlideIndex(0) }}
                                style={{ padding: '6px 12px', borderRadius: '6px', border: 'none', background: activeFilter === 'all' ? '#FF5526' : 'transparent', color: activeFilter === 'all' ? '#FFFFFF' : '#64748B', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}
                            >All Slides</button>
                            <button
                                onClick={() => { setActiveFilter('different'); setCurrentSlideIndex(0) }}
                                style={{ padding: '6px 12px', borderRadius: '6px', border: 'none', background: activeFilter === 'different' ? '#FF5526' : 'transparent', color: activeFilter === 'different' ? '#FFFFFF' : '#64748B', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}
                            >Different Only</button>
                            <button
                                onClick={() => { setActiveFilter('added'); setCurrentSlideIndex(0) }}
                                style={{ padding: '6px 12px', borderRadius: '6px', border: 'none', background: activeFilter === 'added' ? '#FF5526' : 'transparent', color: activeFilter === 'added' ? '#FFFFFF' : '#64748B', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}
                            >Added</button>
                            <button
                                onClick={() => { setActiveFilter('removed'); setCurrentSlideIndex(0) }}
                                style={{ padding: '6px 12px', borderRadius: '6px', border: 'none', background: activeFilter === 'removed' ? '#FF5526' : 'transparent', color: activeFilter === 'removed' ? '#FFFFFF' : '#64748B', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}
                            >Removed</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CompareVersionsModal;
